import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Spin, 
  Alert, 
  Button, 
  Card, 
  Input, 
  Select, 
  Table, 
  Space, 
  Typography,
  Tag,
  message,
  Tooltip,
  Divider,
} from 'antd';
import { 
  ReloadOutlined, 
  CopyOutlined,
  UserOutlined,
  DollarOutlined,
  SoundOutlined,
  FileTextOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useGetTasksQuery } from '../../redux/api/tasksApi';
import { useGetInternalTasksQuery } from '../../redux/api/internalTasksApi';
import Pagination from '../../components/Pagination';

const { Title, Text } = Typography;

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

const PRIORITY_CONFIG = {
  urgent: { color: 'red', label: 'Urgent' },
  high: { color: 'gold', label: 'High' },
  normal: { color: 'default', label: 'Normal' },
};

const STATUS_CONFIG = {
  pending: { color: 'orange', label: 'Pending' },
  in_progress: { color: 'blue', label: 'In Progress' },
  completed: { color: 'green', label: 'Completed' },
  cancelled: { color: 'default', label: 'Cancelled' },
};

const ENTITY_LABELS = {
  proposal: 'Proposal',
  order: 'Order',
  person: 'Person',
  product: 'Product',
};

const ENTITY_PATHS = {
  proposal: '/proposals',
  order: '/orders',
  person: '/persons',
  product: '/products',
};

/**
 * TasksPage - Display and manage Zendesk Sell tasks and internal voice note tasks
 */
const TasksPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read URL params with defaults
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || '10', 10);
  const searchParam = searchParams.get('search') || '';
  const statusParam = searchParams.get('status') || '';
  const ownerParam = searchParams.get('owner') || '';
  const sortBy = searchParams.get('sort_by') || 'updated_at';
  const sortDirection = searchParams.get('sort_direction') || 'desc';

  // Local state for search input (debounced)
  const [searchInput, setSearchInput] = useState(searchParam);

  // Internal tasks filter state
  const [internalStatusFilter, setInternalStatusFilter] = useState('');
  const [internalPriorityFilter, setInternalPriorityFilter] = useState('');
  const [internalSearch, setInternalSearch] = useState('');
  const [internalSearchInput, setInternalSearchInput] = useState('');

  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync local search input with URL param
  useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  // Debounce search input to URL params
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchParam) {
        updateSearchParams({ search: searchInput || null, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchParam, updateSearchParams]);

  // Debounce internal search
  useEffect(() => {
    const timer = setTimeout(() => {
      setInternalSearch(internalSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [internalSearchInput]);

  // Fetch tasks data with server-side pagination/filtering/sorting
  const { data, isLoading, error, refetch, isFetching } = useGetTasksQuery({
    page,
    limit,
    search: searchParam || undefined,
    status: statusParam || undefined,
    owner: ownerParam || undefined,
    sort_by: sortBy,
    sort_direction: sortDirection,
  });

  // Fetch internal tasks (from voice notes)
  const { 
    data: internalTasksData, 
    isLoading: internalTasksLoading, 
    error: internalTasksError, 
    refetch: refetchInternal, 
    isFetching: internalTasksFetching 
  } = useGetInternalTasksQuery({
    status: internalStatusFilter || undefined,
    priority: internalPriorityFilter || undefined,
  });

  // Extract tasks and pagination from response
  const tasks = useMemo(() => data?.tasks || [], [data?.tasks]);
  const pagination = useMemo(() => data?.pagination || {
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1
  }, [data?.pagination]);

  // Internal tasks: filter client-side by search string
  const allInternalTasks = useMemo(() => Array.isArray(internalTasksData) ? internalTasksData : [], [internalTasksData]);
  const internalTasks = useMemo(() => {
    if (!internalSearch) return allInternalTasks;
    const q = internalSearch.toLowerCase();
    return allInternalTasks.filter(t =>
      (t.title && t.title.toLowerCase().includes(q)) ||
      (t.description && t.description.toLowerCase().includes(q)) ||
      (t.due_hint && t.due_hint.toLowerCase().includes(q)) ||
      (t.created_by_first_name && t.created_by_first_name.toLowerCase().includes(q)) ||
      (t.created_by_last_name && t.created_by_last_name.toLowerCase().includes(q)) ||
      (t.assigned_to_first_name && t.assigned_to_first_name.toLowerCase().includes(q)) ||
      (t.assigned_to_last_name && t.assigned_to_last_name.toLowerCase().includes(q))
    );
  }, [allInternalTasks, internalSearch]);

  // Handle page change
  const handlePageChange = (newPage) => {
    updateSearchParams({ page: newPage });
  };

  // Handle page size change
  const handlePageSizeChange = (newLimit) => {
    updateSearchParams({ limit: newLimit, page: 1 });
  };

  // Handle search input change (debounced via useEffect)
  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  // Handle filter changes
  const handleStatusFilterChange = (value) => {
    updateSearchParams({ status: value || null, page: 1 });
  };

  const handleOwnerFilterChange = (value) => {
    updateSearchParams({ owner: value || null, page: 1 });
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchParams({}, { replace: true });
  };

  // Copy tasks to clipboard (uses current filtered results from API)
  const handleCopyTasks = () => {
    if (tasks.length === 0) {
      message.info('No tasks to copy.');
      return;
    }

    const taskContents = tasks
      .map(task => task.content || '')
      .filter(content => content.trim() !== '')
      .join('\n');

    navigator.clipboard.writeText(taskContents)
      .then(() => {
        message.success(`Copied ${tasks.length} tasks to clipboard!`);
      })
      .catch(err => {
        message.error('Failed to copy tasks.');
        console.error('Failed to copy: ', err);
      });
  };

  // Internal tasks columns
  const internalTaskColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '20%',
      render: (text) => <Text strong>{text || '-'}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '18%',
      render: (text) => text ? (
        <Tooltip title={text}>
          <Text style={{ cursor: 'default' }}>
            {text.length > 80 ? text.slice(0, 80) + '…' : text}
          </Text>
        </Tooltip>
      ) : <Text type="secondary">-</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '9%',
      render: (status) => {
        const cfg = STATUS_CONFIG[status] || { color: 'default', label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: '9%',
      render: (priority) => {
        const cfg = PRIORITY_CONFIG[priority] || { color: 'default', label: priority };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Due Date',
      key: 'due_date',
      width: '12%',
      render: (_, record) => {
        if (record.due_date) return formatDate(record.due_date);
        if (record.due_hint) return <Text type="secondary" italic>{record.due_hint}</Text>;
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Linked To',
      key: 'entity',
      width: '10%',
      render: (_, record) => {
        if (!record.entity_type) return <Text type="secondary">-</Text>;
        const label = ENTITY_LABELS[record.entity_type] || record.entity_type;
        const basePath = ENTITY_PATHS[record.entity_type];
        if (basePath && record.entity_id) {
          return (
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={() => navigate(`${basePath}/${record.entity_id}`)}
              style={{ padding: 0, height: 'auto' }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>#{record.entity_id}</span>
                <Text type="secondary" style={{ fontSize: '11px' }}>{label}</Text>
              </span>
            </Button>
          );
        }
        return (
          <div>
            <div>{record.entity_id ? `#${record.entity_id}` : '-'}</div>
            <Text type="secondary" style={{ fontSize: '11px' }}>{label}</Text>
          </div>
        );
      },
    },
    {
      title: 'Author',
      key: 'created_by',
      width: '10%',
      render: (_, record) => {
        const name = [record.created_by_first_name, record.created_by_last_name]
          .filter(Boolean).join(' ');
        return name || <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Assigned To',
      key: 'assigned_to',
      width: '10%',
      render: (_, record) => {
        const name = [record.assigned_to_first_name, record.assigned_to_last_name]
          .filter(Boolean).join(' ');
        return name || <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '11%',
      render: (date) => formatDate(date),
    },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: '11%',
      render: (date) => date ? formatDate(date) : <Text type="secondary">-</Text>,
    },
  ];

  // Table columns configuration
  const columns = [
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      width: '25%',
      render: (text) => <Text>{text || '-'}</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: '10%',
      render: (_, record) => {
        const isCompleted = !!record.done_at;
        return (
          <Tag color={isCompleted ? 'green' : 'orange'}>
            {isCompleted ? 'Completed' : 'Pending'}
          </Tag>
        );
      },
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: '12%',
      render: (text) => text || '-',
    },
    {
      title: 'Linked To',
      key: 'taskable',
      width: '20%',
      render: (_, record) => {
        const taskableType = record.taskable_type?.toLowerCase();
        
        // Build name from available fields (check for non-empty values)
        let name = null;
        
        // For Deal tasks: use deal_name from JOIN
        if (taskableType === 'deal') {
          if (record.deal_name && record.deal_name.trim()) {
            name = record.deal_name;
          } else if (record.taskable_name && record.taskable_name.trim()) {
            name = record.taskable_name;
          }
        }
        // For Contact tasks: prioritize person name, then contact name, then task fields
        else if (taskableType === 'contact') {
          // 1. Person name (from persons table via JOIN)
          if (record.person_first_name || record.person_last_name) {
            name = [record.person_first_name, record.person_last_name]
              .filter(n => n && n.trim())
              .join(' ');
          }
          // 2. Contact full name (from zendesk_contacts table)
          if ((!name || !name.trim()) && record.contact_full_name && record.contact_full_name.trim()) {
            name = record.contact_full_name;
          }
          // 3. Email (person or contact)
          if ((!name || !name.trim()) && (record.person_email || record.contact_email)) {
            name = record.person_email || record.contact_email;
          }
          // 4. Taskable name from task itself
          if ((!name || !name.trim()) && record.taskable_name && record.taskable_name.trim()) {
            name = record.taskable_name;
          }
          // 5. Taskable first/last name from task itself
          if ((!name || !name.trim()) && (record.taskable_first_name || record.taskable_last_name)) {
            name = [record.taskable_first_name, record.taskable_last_name]
              .filter(n => n && n.trim())
              .join(' ');
          }
          // 6. Company name from task itself
          if ((!name || !name.trim()) && record.taskable_company_name && record.taskable_company_name.trim()) {
            name = record.taskable_company_name;
          }
        }
        // For other types: use taskable_name or other fields
        else {
          if (record.taskable_name && record.taskable_name.trim()) {
            name = record.taskable_name;
          } else if (record.taskable_first_name || record.taskable_last_name) {
            name = [record.taskable_first_name, record.taskable_last_name]
              .filter(n => n && n.trim())
              .join(' ');
          } else if (record.taskable_company_name && record.taskable_company_name.trim()) {
            name = record.taskable_company_name;
          }
        }
        
        // Fallback: show taskable_id ONLY if we truly have no name
        if (!name || !name.trim()) {
          if (record.taskable_id) {
            name = `#${record.taskable_id}`;
          } else {
            name = 'Unknown';
          }
        }
        
        // For Deal tasks - link to the deals page (we don't have individual deal pages)
        if (taskableType === 'deal') {
          return (
            <Button
              type="link"
              icon={<DollarOutlined />}
              onClick={() => navigate('/deals')}
              style={{ padding: 0, height: 'auto' }}
              title={`View deals (Deal: ${name})`}
            >
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{name}</span>
                <Text type="secondary" style={{ fontSize: '11px' }}>Deal</Text>
              </span>
            </Button>
          );
        }
        
        // For Contact tasks - link to the person if we have their ID
        if (taskableType === 'contact') {
          if (record.linked_person_id) {
            return (
              <Button
                type="link"
                icon={<UserOutlined />}
                onClick={() => navigate(`/persons/${record.linked_person_id}`)}
                style={{ padding: 0, height: 'auto' }}
                title="View customer details"
              >
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span>{name}</span>
                  <Text type="secondary" style={{ fontSize: '11px' }}>Contact</Text>
                </span>
              </Button>
            );
          }
          // Contact but no linked person - show as text with type
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <UserOutlined style={{ color: '#999' }} />
                <span>{name}</span>
              </div>
              <Text type="secondary" style={{ fontSize: '11px' }}>Contact (not linked)</Text>
            </div>
          );
        }
        
        // Other types - just show text
        return (
          <div>
            <div>{name}</div>
            {record.taskable_type && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {record.taskable_type}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '12%',
      sorter: true,
      sortOrder: sortBy === 'date' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => formatDate(date),
    },
    {
      title: 'Done At',
      dataIndex: 'done_at',
      key: 'done_at',
      width: '12%',
      sorter: true,
      sortOrder: sortBy === 'done_at' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => date ? formatDate(date) : '-',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '12%',
      sorter: true,
      sortOrder: sortBy === 'created_at' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => formatDate(date),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '12%',
      sorter: true,
      sortOrder: sortBy === 'updated_at' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => formatDate(date),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.field) {
      const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
      updateSearchParams({ sort_by: sorter.field, sort_direction: direction, page: 1 });
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={1} style={{ margin: 0 }}>Tasks</Title>
      </div>

      {/* ── Zendesk Tasks Section ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Zendesk Tasks</Title>
        <Button 
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px', marginBottom: 24 }}>
          <Spin tip="Loading Zendesk tasks..." />
        </div>
      ) : error ? (
        <Alert
          message="Error loading Zendesk tasks"
          description={error?.data?.error || error?.error || 'Failed to load tasks from Zendesk Sell'}
          type="error"
          action={<Button size="small" danger onClick={() => refetch()}>Retry</Button>}
          showIcon
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {/* Zendesk Filters Row */}
      <Card style={{ marginBottom: 24 }}>
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 300px' }}>
              <Text strong>Search</Text>
              <Input
                placeholder="Search by content, taskable name, or owner..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                allowClear
                onClear={() => handleSearchChange('')}
                style={{ marginTop: 4 }}
              />
            </div>
            <div style={{ width: 150 }}>
              <Text strong>Status</Text>
              <Select
                value={statusParam || null}
                onChange={handleStatusFilterChange}
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All statuses"
                allowClear
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'completed', label: 'Completed' },
                ]}
              />
            </div>
            <div style={{ width: 200 }}>
              <Text strong>Owner</Text>
              <Input
                placeholder="Filter by owner..."
                value={ownerParam}
                onChange={(e) => handleOwnerFilterChange(e.target.value || null)}
                allowClear
                style={{ marginTop: 4 }}
              />
            </div>
            <div style={{ width: 150 }}>
              <Text strong>Show</Text>
              <Select
                value={limit}
                onChange={handlePageSizeChange}
                style={{ width: '100%', marginTop: 4 }}
                options={[
                  { value: 10, label: '10 per page' },
                  { value: 25, label: '25 per page' },
                  { value: 50, label: '50 per page' },
                  { value: 100, label: '100 per page' },
                ]}
              />
            </div>
            <div>
              <Button onClick={handleResetFilters}>Reset Filters</Button>
            </div>
            <div>
              <Button 
                icon={<CopyOutlined />}
                onClick={handleCopyTasks}
                disabled={tasks.length === 0}
              >
                Copy {tasks.length} Tasks
              </Button>
            </div>
          </div>
        </Space>
      </Card>

      {/* Results count */}
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          Showing {tasks.length} of {pagination.total} tasks
          {searchParam && ` matching "${searchParam}"`}
          {statusParam && ` (${statusParam} only)`}
          {ownerParam && ` (owner: ${ownerParam})`}
        </Text>
      </div>

      {/* Zendesk Tasks Table */}
      {!isLoading && !error && (
        tasks.length === 0 ? (
          <Alert
            message="No tasks found"
            description={searchParam || statusParam || ownerParam ? 'No tasks match your filters.' : 'No tasks available.'}
            type="info"
            showIcon
          />
        ) : (
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey="id"
            pagination={false}
            onChange={handleTableChange}
            size="middle"
          />
        )
      )}

      {/* Zendesk Pagination */}
      {pagination.total_pages > 1 && (
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          onPageChange={handlePageChange}
          totalItems={pagination.total}
          itemsPerPage={pagination.per_page}
          showInfo={true}
        />
      )}

      {/* ── Internal Tasks Section (Voice Notes) ── */}
      <Divider style={{ marginTop: 48, marginBottom: 32 }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={2} style={{ margin: 0 }}>
            <SoundOutlined style={{ marginRight: 8, color: '#1677ff' }} />
            Voice Note Tasks
          </Title>
          {!internalTasksLoading && (
            <Tag color="blue" style={{ fontSize: 13 }}>
              {internalTasks.length} {internalTasks.length === 1 ? 'task' : 'tasks'}
            </Tag>
          )}
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetchInternal()}
          loading={internalTasksFetching}
        >
          Refresh
        </Button>
      </div>

      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Tasks created from AI-enhanced voice notes on proposals, orders, persons, and products.
      </Text>

      {/* Internal Tasks Filters */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 260px' }}>
            <Text strong>Search</Text>
            <Input
              placeholder="Search by title, description, or author..."
              value={internalSearchInput}
              onChange={(e) => setInternalSearchInput(e.target.value)}
              allowClear
              onClear={() => { setInternalSearchInput(''); setInternalSearch(''); }}
              style={{ marginTop: 4 }}
            />
          </div>
          <div style={{ width: 160 }}>
            <Text strong>Status</Text>
            <Select
              value={internalStatusFilter || null}
              onChange={(v) => setInternalStatusFilter(v || '')}
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All statuses"
              allowClear
              options={[
                { value: 'pending', label: 'Pending' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </div>
          <div style={{ width: 160 }}>
            <Text strong>Priority</Text>
            <Select
              value={internalPriorityFilter || null}
              onChange={(v) => setInternalPriorityFilter(v || '')}
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All priorities"
              allowClear
              options={[
                { value: 'urgent', label: 'Urgent' },
                { value: 'high', label: 'High' },
                { value: 'normal', label: 'Normal' },
              ]}
            />
          </div>
          <div>
            <Button onClick={() => {
              setInternalStatusFilter('');
              setInternalPriorityFilter('');
              setInternalSearchInput('');
              setInternalSearch('');
            }}>
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Internal Tasks Table */}
      {internalTasksLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
          <Spin tip="Loading voice note tasks..." />
        </div>
      ) : internalTasksError ? (
        <Alert
          message="Error loading voice note tasks"
          description={internalTasksError?.data?.error || internalTasksError?.error || 'Failed to load internal tasks'}
          type="error"
          action={<Button size="small" danger onClick={() => refetchInternal()}>Retry</Button>}
          showIcon
        />
      ) : internalTasks.length === 0 ? (
        <Alert
          message="No voice note tasks found"
          description={
            internalSearch || internalStatusFilter || internalPriorityFilter
              ? 'No tasks match your filters.'
              : 'No tasks have been created from voice notes yet. Use the AI Enhance feature on a voice note to generate tasks.'
          }
          type="info"
          icon={<FileTextOutlined />}
          showIcon
        />
      ) : (
        <Table
          columns={internalTaskColumns}
          dataSource={internalTasks}
          rowKey="id"
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (total) => `${total} tasks` }}
          size="middle"
          rowClassName={(record) => record.status === 'completed' ? 'ant-table-row-completed' : ''}
        />
      )}
    </div>
  );
};

export default TasksPage;

